import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Pencil, Calculator, Building2, Loader2, Trash2, KeyRound, Coins } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import CompanyFormDialog from "../components/CompanyFormDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useToast } from "../hooks/use-toast";
import { getCompanies, deleteCompany, listComputations, getMe, redeemToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const statusColor = {
  Filed: "bg-green-100 text-green-700",
  Submitted: "bg-emerald-100 text-emerald-700",
  Pending: "bg-amber-100 text-amber-700",
  Draft: "bg-gray-100 text-gray-600",
};

const CompanyList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [delEmail, setDelEmail] = useState("");
  const [delPassword, setDelPassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [submittedIds, setSubmittedIds] = useState(new Set());
  const [me, setMe] = useState(null);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const cos = await getCompanies();
      setList(cos || []);
      const comps = await listComputations();
      setSubmittedIds(new Set((comps || []).filter((r) => r.locked).map((r) => r.companyId)));
      try { setMe(await getMe()); } catch (e) { /* ignore */ }
    } catch (e) {
      setList([]);
      setSubmittedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openNew = () => { setEditingCompany(null); setOpen(true); };
  const openEdit = (c) => { setEditingCompany(c); setOpen(true); };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteCompany(toDelete.id, delEmail.trim(), delPassword);
      toast({ title: "Company deleted", description: toDelete.name });
      setToDelete(null);
      setConfirmText("");
      setDelEmail("");
      setDelPassword("");
      reload();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not delete company.", variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const filtered = list.filter(
    (c) =>
      (c.name || "").toLowerCase().includes(query.toLowerCase()) ||
      (c.regNo || "").toLowerCase().includes(query.toLowerCase())
  );

  const handleRedeem = async () => {
    if (!redeemCode.trim()) return;
    setRedeeming(true);
    try {
      const res = await redeemToken(redeemCode.trim());
      toast({ title: "Token redeemed", description: `${res.creditsAdded} credit(s) added. Balance: ${res.credits}.` });
      setRedeemOpen(false);
      setRedeemCode("");
      reload();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not redeem token.", variant: "destructive" });
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded bg-[#1877c9] text-white flex items-center justify-center">
          <Building2 size={20} />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Company</h1>
          <p className="text-sm text-gray-500">Manage your client companies and their tax records</p>
        </div>
        {me && !me.isOwner && (
          <div className="ml-auto flex items-center gap-2" data-testid="credit-bar">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-amber-50 text-amber-700 border border-amber-200" data-testid="credit-balance">
              <Coins size={15} /> {me.credits} credit{me.credits === 1 ? "" : "s"}
            </span>
            <Button variant="outline" onClick={() => setRedeemOpen(true)} className="gap-2 h-9 border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]" data-testid="redeem-token-btn">
              <KeyRound size={15} /> Redeem token
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-sm shadow-sm">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-b border-gray-100">
          <div className="relative w-full sm:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search company or reg. no..."
              className="pl-9"
            />
          </div>
          <Button onClick={openNew} className="bg-[#1877c9] hover:bg-[#0f5ea3] gap-2 w-full sm:w-auto">
            <Plus size={16} /> New Company
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f7f9fc] text-gray-600 text-left">
                <th className="px-4 py-3 font-medium">Income Tax Ref</th>
                <th className="px-4 py-3 font-medium">Company Name</th>
                <th className="px-4 py-3 font-medium">Reg. No.</th>
                <th className="px-4 py-3 font-medium">Form</th>
                <th className="px-4 py-3 font-medium">YA</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Filing</th>
                {isAdmin && <th className="px-4 py-3 font-medium">User</th>}
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-[#f9fbfe]">
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{c.refNo}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/company/${c.id}/tax`)}
                      title="Open tax computation"
                      className="font-medium text-gray-800 hover:text-[#1877c9] hover:underline text-left"
                      data-testid={`open-company-${c.id}`}
                    >
                      {c.name}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.regNo}</td>
                  <td className="px-4 py-3">{c.form}</td>
                  <td className="px-4 py-3">{c.ya}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${c.status === "Active" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const fs = submittedIds.has(c.id) ? "Submitted" : c.filingStatus;
                      return (
                        <span className={`px-2 py-0.5 rounded-full text-xs ${statusColor[fs] || "bg-gray-100 text-gray-600"}`}>
                          {fs}
                        </span>
                      );
                    })()}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-gray-600" data-testid={`company-owner-${c.id}`}>
                      {c.ownerName || c.ownerEmail || "—"}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(c)}
                        title="Edit company"
                        className="p-1.5 text-gray-500 hover:text-[#1877c9] hover:bg-blue-50 rounded"
                        data-testid={`edit-company-${c.id}`}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => navigate(`/company/${c.id}/tax`)}
                        title="Tax computation"
                        className="p-1.5 text-gray-500 hover:text-[#1877c9] hover:bg-blue-50 rounded"
                        data-testid={`open-tax-${c.id}`}
                      >
                        <Calculator size={16} />
                      </button>
                      <button
                        onClick={() => { setToDelete(c); setConfirmText(""); }}
                        title="Delete company"
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                        data-testid={`delete-company-${c.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 9 : 8} className="px-4 py-10 text-center text-gray-400">
                    {loading ? (<><Loader2 className="inline animate-spin mr-2" size={16} />Loading companies…</>) : "No companies found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CompanyFormDialog open={open} onOpenChange={setOpen} company={editingCompany} onSaved={reload} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) { setToDelete(null); setConfirmText(""); setDelEmail(""); setDelPassword(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete company?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `This permanently removes "${toDelete.name}" and its tax computations will no longer be reachable from here. This cannot be undone.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1 space-y-3">
            <div>
              <p className="text-sm text-gray-600 mb-2">Enter your User ID and password to authorise this deletion:</p>
              <Input
                value={delEmail}
                onChange={(e) => setDelEmail(e.target.value)}
                placeholder="User ID (email)"
                autoComplete="off"
                className="mb-2"
                data-testid="delete-email-input"
              />
              <Input
                type="password"
                value={delPassword}
                onChange={(e) => setDelPassword(e.target.value)}
                placeholder="Password"
                autoComplete="new-password"
                data-testid="delete-password-input"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={
                !toDelete ||
                deleting ||
                !delEmail.trim() ||
                !delPassword
              }
              className="bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:pointer-events-none"
              data-testid="confirm-delete-company"
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={redeemOpen} onOpenChange={(o) => { if (!o) { setRedeemOpen(false); setRedeemCode(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Redeem access token</AlertDialogTitle>
            <AlertDialogDescription>
              Enter the token code you received after purchase. Each credit lets you create one tax file (company).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1">
            <Input
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              placeholder="SPEC-XXXX-XXXX-XXXX"
              className="font-mono"
              data-testid="redeem-code-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRedeem}
              disabled={redeeming || !redeemCode.trim()}
              className="bg-[#1877c9] hover:bg-[#0f5ea3] disabled:opacity-40 disabled:pointer-events-none"
              data-testid="confirm-redeem-token"
            >
              {redeeming ? "Redeeming…" : "Redeem"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanyList;
